import { ForwardedRef, useImperativeHandle, useMemo, useState } from "react";
import {
  useSelectFieldLoading,
  DEFAULT_PAGE_SIZE,
} from "./useSelectFieldLoading";
import { useAdaptiveView } from "../../beta/hooks/useAdaptiveView";

export type UseSelectOrchestrationConfig = {
  lazy: false | "page" | "offset" | "group" | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loadOptions: any;
  cache?: { enabled?: boolean; maxSize?: number };
  lazyOptions?: { pageSize?: number; limit?: number };
  initialLoad?: "auto" | "immediate" | "open";
  displayMenuAs?: "auto" | "popover" | "dialog";
  ref: ForwardedRef<{ clearCache: () => void; invalidate: () => void }>;
};

/**
 * Shared orchestration hook used by Select components
 * Centralizes adaptive view resolution, loading config construction, option loading lifecycle,
 * and imperative handle setup (clearCache/invalidate).
 */
export function useSelectOrchestration(config: UseSelectOrchestrationConfig) {
  const {
    lazy,
    loadOptions: loadOptionsProp,
    cache,
    lazyOptions,
    initialLoad: initialLoadProp = "auto",
    displayMenuAs: displayMenuAsProp = "auto",
    ref,
  } = config;

  const [initialLoadPerformed, setInitialLoadPerformed] = useState(false);

  const { isMobile } = useAdaptiveView();
  const displayAs =
    displayMenuAsProp === "auto"
      ? isMobile
        ? "dialog"
        : "popover"
      : displayMenuAsProp;

  const initialLoad =
    initialLoadProp === "auto" ? "immediate" : initialLoadProp;

  const pageSize =
    lazy === "page"
      ? (lazyOptions?.pageSize ?? DEFAULT_PAGE_SIZE)
      : DEFAULT_PAGE_SIZE;
  const limit =
    lazy === "offset"
      ? (lazyOptions?.limit ?? DEFAULT_PAGE_SIZE)
      : DEFAULT_PAGE_SIZE;

  const loadingHookConfig = useMemo(() => {
    const cacheConfig = {
      enabled: cache?.enabled,
      maxSize: cache?.maxSize,
    };
    if (lazy === "page")
      return {
        lazy: "page" as const,
        loadOptions: loadOptionsProp,
        pageSize,
        cache: cacheConfig,
      };
    if (lazy === "offset")
      return {
        lazy: "offset" as const,
        loadOptions: loadOptionsProp,
        limit,
        cache: cacheConfig,
      };
    if (lazy === "group")
      return {
        lazy: "group" as const,
        loadOptions: loadOptionsProp,
        cache: cacheConfig,
      };
    return {
      lazy: false as const,
      loadOptions: loadOptionsProp,
      cache: cacheConfig,
    };
  }, [lazy, loadOptionsProp, pageSize, limit, cache?.enabled, cache?.maxSize]);

  const {
    options,
    loading,
    loadingMore,
    hasMore,
    loadOptions,
    loadMore,
    clearCache,
  } = useSelectFieldLoading(loadingHookConfig);

  useImperativeHandle(
    ref,
    () => ({
      clearCache,
      invalidate: () => {
        clearCache();
        setInitialLoadPerformed(false);
      },
    }),
    [clearCache],
  );

  return {
    displayAs,
    initialLoad,
    initialLoadPerformed,
    setInitialLoadPerformed,
    options,
    loading,
    loadingMore,
    hasMore,
    loadOptions,
    loadMore,
  };
}
